import logger from '../../lib/logger';
import React, { useState } from 'react';
import { Calendar, Clock, CalendarPlus, Edit2, Trash2, Loader2, Check, Image, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const EVENT_TYPES = [
  { value: "general", label: "General / Community" },
  { value: "youth", label: "Youth Program" },
  { value: "madrassah", label: "Madrassah" },
  { value: "religious", label: "Religious" },
  { value: "fundraising", label: "Fundraising" }
];

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ direction: 'rtl' }],
    ['clean']
  ]
};

const EventForm = ({ event, setEvent, onSave, onCancel, isEditing, uploadingPoster, onUploadPoster }) => {
  // Track if Quill has loaded to avoid SSR issues
  const [quillReady, setQuillReady] = useState(true);

  return (
  <div className="p-6 bg-[#f8f6f1] border-b">
    <h3 className="text-lg text-[#1a2f23] mb-4">{isEditing ? 'Edit Event' : 'Create New Event'}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2">
        <Label className="text-sm text-[#5a6d62]">Title *</Label>
        <Input value={event.title} onChange={(e) => setEvent({...event, title: e.target.value})} className="mt-1" placeholder="Event title" />
      </div>
      <div>
        <Label className="text-sm text-[#5a6d62]">Type *</Label>
        <Select value={event.event_type} onValueChange={(v) => setEvent({...event, event_type: v})}>
          <SelectTrigger className="mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
          <SelectContent>{EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-sm text-[#5a6d62]">Date *</Label>
        <Input type="date" value={event.date} onChange={(e) => setEvent({...event, date: e.target.value})} className="mt-1" />
      </div>
      <div>
        <Label className="text-sm text-[#5a6d62]">Time *</Label>
        <Input value={event.time} onChange={(e) => setEvent({...event, time: e.target.value})} className="mt-1" placeholder="e.g., 7:00 PM" />
      </div>
      <div>
        <Label className="text-sm text-[#5a6d62]">Location *</Label>
        <Input value={event.location} onChange={(e) => setEvent({...event, location: e.target.value})} className="mt-1" placeholder="Event location" />
      </div>
      <div className="lg:col-span-3">
        <Label className="text-sm text-[#5a6d62]">Description *</Label>
        <div className="mt-1 bg-white">
          <ReactQuill
            theme="snow"
            value={event.description || ''}
            onChange={(val) => setEvent({...event, description: val})}
            modules={QUILL_MODULES}
            placeholder="Event description — use the toolbar for bold, lists, RTL, etc."
          />
        </div>
      </div>
      <div className="lg:col-span-3">
        <Label className="text-sm text-[#5a6d62]">Event Poster</Label>
        <div className="mt-1 flex items-center gap-4">
          <input type="file" id={isEditing ? 'edit-poster' : 'new-poster'} accept="image/*" onChange={(e) => e.target.files[0] && onUploadPoster(e.target.files[0])} className="hidden" />
          <Button type="button" variant="outline" onClick={() => document.getElementById(isEditing ? 'edit-poster' : 'new-poster').click()} disabled={uploadingPoster}>
            {uploadingPoster ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Image className="w-4 h-4" /> Upload Poster</>}
          </Button>
          {event.poster_url && <img src={event.poster_url} alt="Poster preview" className="h-20 rounded border" />}
          {event.poster_url && <button type="button" onClick={() => setEvent({...event, poster_url: ''})} className="text-red-600 text-sm">Remove</button>}
        </div>
      </div>
    </div>
    <div className="flex gap-2 mt-4">
      <Button onClick={onSave} className="bg-green-600 hover:bg-green-700 text-white" disabled={!event.title || !event.date || !event.time || !event.description || event.description === '<p><br></p>' || !event.location}><Check className="w-4 h-4" /> {isEditing ? 'Update Event' : 'Create Event'}</Button>
      <Button onClick={onCancel} variant="outline">Cancel</Button>
    </div>
  </div>
  );
};

const EventsTab = ({ events, eventStats, loadingData, token, apiBaseUrl, onRefresh, onStatsRefresh, onDashboardRefresh }) => {
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({ title: '', date: '', time: '', description: '', location: '', event_type: 'general', poster_url: '' });
  const [uploadingPoster, setUploadingPoster] = useState(false);

  const uploadPoster = async (file) => {
    setUploadingPoster(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${apiBaseUrl}/api/admin/upload-poster`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${apiBaseUrl}${data.url}`;
        if (editingEvent) setEditingEvent({ ...editingEvent, poster_url: fullUrl });
        else setNewEvent(prev => ({ ...prev, poster_url: fullUrl }));
      }
    } catch (err) { logger.error('Failed to upload poster:', err); }
    finally { setUploadingPoster(false); }
  };

  const createEvent = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/events`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(newEvent) });
      if (response.ok) {
        setNewEvent({ title: '', date: '', time: '', description: '', location: '', event_type: 'general', poster_url: '' });
        setShowAddEvent(false);
        onRefresh(); onStatsRefresh(); onDashboardRefresh();
      }
    } catch (err) { logger.error('Failed to create event:', err); }
  };

  const updateEvent = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/events/${editingEvent.id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(editingEvent) });
      if (response.ok) { setEditingEvent(null); onRefresh(); onStatsRefresh(); }
    } catch (err) { logger.error('Failed to update event:', err); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await fetch(`${apiBaseUrl}/api/admin/events/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      onRefresh(); onStatsRefresh(); onDashboardRefresh();
    } catch (err) { logger.error('Failed to delete event:', err); }
  };

  const toggleEventActive = async (id, currentStatus) => {
    try {
      await fetch(`${apiBaseUrl}/api/admin/events/${id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ is_active: !currentStatus }) });
      onRefresh(); onStatsRefresh();
    } catch (err) { logger.error('Failed to toggle event:', err); }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6">
      {eventStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 shadow border-l-4 border-purple-500"><p className="text-sm text-[#5a6d62]">Total Events</p><p className="text-2xl font-bold text-[#1a2f23]">{eventStats.total}</p></div>
          <div className="bg-white p-4 shadow border-l-4 border-green-500"><p className="text-sm text-[#5a6d62]">Active</p><p className="text-2xl font-bold text-green-600">{eventStats.active}</p></div>
          <div className="bg-white p-4 shadow border-l-4 border-gray-400"><p className="text-sm text-[#5a6d62]">Inactive</p><p className="text-2xl font-bold text-gray-500">{eventStats.inactive}</p></div>
          <div className="bg-white p-4 shadow border-l-4 border-[#c5a572]"><p className="text-sm text-[#5a6d62]">Types</p><p className="text-2xl font-bold text-[#c5a572]">{eventStats.by_type?.length || 0}</p></div>
        </div>
      )}
      <div className="bg-white shadow-lg">
        <div className="p-6 border-b flex justify-between items-center">
          <div><h2 className="text-xl text-[#1a2f23]">Events & Programs</h2><p className="text-sm text-[#5a6d62]">Manage events shown on the website</p></div>
          <Button onClick={() => setShowAddEvent(true)} className="btn-primary"><CalendarPlus className="w-4 h-4" /> Add Event</Button>
        </div>
        {showAddEvent && <EventForm event={newEvent} setEvent={setNewEvent} onSave={createEvent} onCancel={() => setShowAddEvent(false)} isEditing={false} uploadingPoster={uploadingPoster} onUploadPoster={uploadPoster} />}
        {editingEvent && <EventForm event={editingEvent} setEvent={setEditingEvent} onSave={updateEvent} onCancel={() => setEditingEvent(null)} isEditing={true} uploadingPoster={uploadingPoster} onUploadPoster={uploadPoster} />}
        {loadingData ? <div className="p-8 text-center"><Loader2 className="w-8 h-8 text-[#c5a572] animate-spin mx-auto" /></div> : events.length === 0 ? <div className="p-8 text-center text-[#5a6d62]">No events yet. Create your first event!</div> : (
          <div className="divide-y divide-gray-100">
            {events.map(event => {
              const isPast = event.date < today;
              return (
                <div key={event.id} className={`p-6 ${!event.is_active ? 'bg-gray-50 opacity-60' : ''}`}>
                  <div className="flex gap-6">
                    {event.poster_url && <img src={event.poster_url} alt={event.title} className="w-32 h-24 object-cover rounded border" />}
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-medium text-[#1a2f23]">{event.title}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            <span className={`text-xs px-2 py-1 rounded ${event.event_type === 'youth' ? 'bg-blue-100 text-blue-800' : event.event_type === 'madrassah' ? 'bg-green-100 text-green-800' : event.event_type === 'religious' ? 'bg-purple-100 text-purple-800' : 'bg-[#c5a572]/20 text-[#8a7045]'}`}>
                              {EVENT_TYPES.find(t => t.value === event.event_type)?.label || 'General'}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded ${event.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{event.is_active ? 'Active' : 'Hidden'}</span>
                            {isPast && <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-700">Archived (past date)</span>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setEditingEvent(event)} className="text-[#c5a572] hover:text-[#8a7045]"><Edit2 className="w-5 h-5" /></button>
                          <button onClick={() => deleteEvent(event.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-5 h-5" /></button>
                        </div>
                      </div>
                      <div className="text-sm text-[#5a6d62] mt-2 line-clamp-3 rich-text-content" dangerouslySetInnerHTML={{ __html: event.description }} />
                      <div className="flex items-center gap-4 mt-3 text-sm text-[#5a6d62]">
                        <span><Calendar className="w-4 h-4 inline mr-1" />{event.date}</span>
                        <span><Clock className="w-4 h-4 inline mr-1" />{event.time}</span>
                        <span>{event.location}</span>
                      </div>
                      <div className="mt-3">
                        <button onClick={() => toggleEventActive(event.id, event.is_active)} className={`text-xs px-3 py-1 rounded ${event.is_active ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-700'}`}>
                          {event.is_active ? 'Hide Event' : 'Show Event'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsTab;
